import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ShoppingCart, BookOpen, Download, Star, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useSiteData } from '../contexts/SiteDataContext';

const BookSection = () => {
  const { books } = useSiteData();
  const bookData = books[0] || { highlights: [], tableOfContents: [], benefits: [] };
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const [selectedTab, setSelectedTab] = useState('overview');

  return (
    <section id="book-section" ref={ref} className="py-24 bg-gradient-to-br from-white via-orange-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-orange-400 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
            पुस्तक परिचय
          </h2>
          <p 
            className="text-xl text-orange-600 font-semibold"
            style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
          >
            नवभारत का शंखनाद
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Left: 3D Book Cover */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <motion.div
              className="relative w-full max-w-lg mx-auto"
              whileHover={{ 
                scale: 1.05,
                rotateY: 10,
                transition: { duration: 0.3 }
              }}
              style={{ perspective: 1000 }}
            >
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400/50 to-blue-600/50 blur-3xl opacity-60 animate-pulse" />
              
              {/* Book Image with 3D Effect */}
              <motion.div
                className="relative rounded-2xl overflow-hidden shadow-2xl"
                animate={{
                  rotateY: [0, 5, 0, -5, 0],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <img 
                  src={bookData.coverImage}
                  alt={bookData.title}
                  className="w-full h-auto object-cover"
                />
              </motion.div>

              {/* Floating Rating Badge */}
              <motion.div
                className="absolute -bottom-6 -right-6 bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-2xl shadow-2xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <div className="flex items-center gap-2 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-current" />
                  ))}
                </div>
                <div className="text-center text-sm font-semibold" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>4.8/5 रेटिंग</div>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right: Book Details */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <h3 
              className="text-3xl md:text-4xl font-bold text-blue-900"
              style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
            >
              {bookData.title}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>
              {bookData.description}
            </p>

            {/* Book Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-orange-100">
                <div className="text-sm text-gray-600" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>पृष्ठ</div>
                <div className="text-2xl font-bold text-blue-900">{bookData.pages}</div>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-orange-100">
                <div className="text-sm text-gray-600" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>भाषा</div>
                <div className="text-2xl font-bold text-blue-900" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{bookData.language}</div>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-orange-100">
                <div className="text-sm text-gray-600" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>प्रकाशक</div>
                <div className="text-lg font-bold text-blue-900" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>{bookData.publisher}</div>
              </div>
              <div className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-orange-100">
                <div className="text-sm text-gray-600" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>मूल्य</div>
                <div className="text-2xl font-bold text-orange-600">{bookData.price}</div>
              </div>
            </div>

            {/* Buy Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                onClick={() => window.open(bookData.amazonLink, '_blank')}
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Amazon पर खरीदें
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white transition-all duration-300 hover:scale-105"
                onClick={() => window.open(bookData.flipkartLink, '_blank')}
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Flipkart पर खरीदें
              </Button>
            </div>
            <Button 
              size="lg"
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg"
              onClick={() => window.open(bookData.garudaLink, '_blank')}
            >
              <BookOpen className="mr-2 h-5 w-5" />
              गरुड़ प्रकाशन से खरीदें
            </Button>
          </motion.div>
        </div>

        {/* Tabs Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {/* Tab Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {[
              { id: 'overview', label: 'Key Highlights', labelHindi: 'मुख्य बिंदु' },
              { id: 'toc', label: 'Table of Contents', labelHindi: 'विषय सूची' },
              { id: 'benefits', label: 'Benefits', labelHindi: 'लाभ' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  selectedTab === tab.id
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg scale-105'
                    : 'bg-white/70 backdrop-blur-sm text-gray-700 hover:bg-orange-100 border border-orange-100'
                }`}
              >
                {tab.labelHindi}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 border border-orange-100 shadow-xl">
            {selectedTab === 'overview' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="grid md:grid-cols-2 gap-6"
              >
                {bookData.highlights.map((highlight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3 group"
                  >
                    <CheckCircle className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" />
                    <p 
                      className="text-gray-700 text-lg"
                      style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                    >
                      {highlight}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {selectedTab === 'toc' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="space-y-4"
              >
                {bookData.tableOfContents.map((chapter, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-50 to-transparent rounded-xl hover:from-orange-100 transition-all duration-300 group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white font-bold group-hover:scale-110 transition-transform">
                      {index + 1}
                    </div>
                    <p 
                      className="text-gray-800 text-lg font-medium"
                      style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                    >
                      {chapter}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {selectedTab === 'benefits' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="grid md:grid-cols-2 gap-6"
              >
                {(bookData.benefits || []).map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gradient-to-br from-blue-50 to-orange-50 p-6 rounded-xl border-2 border-orange-200 hover:border-orange-400 transition-all duration-300 hover:shadow-lg"
                  >
                    <p 
                      className="text-gray-800 text-base font-medium"
                      style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
                    >
                      {benefit}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Download Brochure Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-2xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>पुस्तक ब्रोशर डाउनलोड करें</h3>
            <p className="text-blue-100 mb-6" style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}>पुस्तक की विस्तृत जानकारी PDF प्रारूप में प्राप्त करें</p>
            <Button 
              size="lg"
              className="bg-white text-blue-900 hover:bg-gray-100 shadow-lg hover:scale-105 transition-all duration-300"
              style={{ fontFamily: '"Noto Sans Devanagari", sans-serif' }}
            >
              <Download className="mr-2 h-5 w-5" />
              ब्रोशर डाउनलोड करें (PDF)
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BookSection;